import {
  DEFAULT_UI_LANGUAGE_CODE,
  SELECTABLE_UI_LANGUAGE_CODES,
  normalizeUiLanguageCode,
} from '../constants/uiLanguages';

const UI_LANGUAGE_BOUND_ROUTE_ROOTS = new Set([
  'store',
  'faq',
  'contact',
  'feedback',
  'technical-support',
  'blog',
  'privacy',
  'terms',
  'cookies',
]);

// Public marketing pages whose unprefixed URL is the English version. Visitors
// with no saved language preference (first-time visitors and search engines)
// see these in English instead of the browser or default learning language.
const UNPREFIXED_ENGLISH_MARKETING_ROOTS = new Set([
  'blog',
  'coach',
  'contact',
  'cookies',
  'faq',
  'feedback',
  'learn-english',
  'learning-guide',
  'learning-paths',
  'learning-tips',
  'library',
  'listening-practice',
  'meet-creator',
  'methodology',
  'podcast-lessons',
  'privacy',
  'store',
  'technical-support',
  'terms',
]);

export function normalizeRoutePath(pathname) {
  const rawPath = String(pathname || '/').trim();
  if (!rawPath || rawPath === '/') return '/';

  const normalized = rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
  return normalized || '/';
}

export function getPathBoundUiLanguage(pathname) {
  const normalizedPath = normalizeRoutePath(pathname);
  if (normalizedPath === '/') {
    return DEFAULT_UI_LANGUAGE_CODE;
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length === 0) {
    return DEFAULT_UI_LANGUAGE_CODE;
  }

  const normalizedLanguage = normalizeUiLanguageCode(segments[0], '');
  if (!normalizedLanguage || !SELECTABLE_UI_LANGUAGE_CODES.includes(normalizedLanguage)) {
    return null;
  }

  if (segments.length === 1) {
    return normalizedLanguage;
  }

  return UI_LANGUAGE_BOUND_ROUTE_ROOTS.has(String(segments[1] || '').toLowerCase())
    ? normalizedLanguage
    : null;
}

// The language a URL implies for a visitor with no saved preference. Unlike
// getPathBoundUiLanguage, this never overrides a preference the visitor has.
export function getUrlDefaultUiLanguage(pathname) {
  const pathBoundLanguage = getPathBoundUiLanguage(pathname);
  if (pathBoundLanguage) {
    return pathBoundLanguage;
  }

  const firstSegment = String(normalizeRoutePath(pathname).split('/')[1] || '').toLowerCase();
  return UNPREFIXED_ENGLISH_MARKETING_ROOTS.has(firstSegment) ? DEFAULT_UI_LANGUAGE_CODE : null;
}

export function isHomepageUiPath(pathname) {
  const normalizedPath = normalizeRoutePath(pathname);
  if (normalizedPath === '/') {
    return true;
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length !== 1) {
    return false;
  }

  const normalizedLanguage = normalizeUiLanguageCode(segments[0], '');
  return Boolean(normalizedLanguage && SELECTABLE_UI_LANGUAGE_CODES.includes(normalizedLanguage));
}

export function buildHomepagePathForUiLanguage(languageCode) {
  const normalizedLanguage = normalizeUiLanguageCode(languageCode);
  return normalizedLanguage === DEFAULT_UI_LANGUAGE_CODE ? '/' : `/${normalizedLanguage}`;
}
